import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime, num } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { OperationType, Page, UasOperationApplication } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const ANY_OFFICER = ['UAS_OFFICER', 'AIRSPACE_OFFICER', 'FLIGHT_OPS_INSPECTOR'];
const REVIEW_ROLES = ['UAS_OFFICER', 'AIRSPACE_OFFICER'];
const INSPECTOR_ROLES = ['FLIGHT_OPS_INSPECTOR'];
const OPERATION_TYPES: OperationType[] = ['VLOS_STANDARD', 'BVLOS', 'NIGHT', 'CONTROLLED_AIRSPACE'];
const STATES = ['APPLIED', 'UNDER_REVIEW', 'RISK_ASSESSMENT', 'OPS_MANUAL_REVIEW', 'TRIAL_FLIGHT', 'FEE_PENDING', 'AUTHORIZED', 'REJECTED', 'SUSPENDED', 'REVOKED', 'EXPIRED', 'WITHDRAWN'];

/**
 * M23 — UAS operation authorisation applications: the synchronous no-fly-
 * zone + C9 AVIATION_HEIGHT airspace check runs at apply time (folded into
 * the POST itself, not its own persisted stage); VLOS_STANDARD/NIGHT/
 * CONTROLLED_AIRSPACE go through a single UNDER_REVIEW decision, while
 * BVLOS walks the full SORA-lite -> ops-manual review -> Ezzy Courier
 * trial-flight pathway before fee/authorisation.
 */
export function UasOperations() {
  const { persona } = useAuth();
  const canView = persona ? persona.roles.some((r) => ANY_OFFICER.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [operationType, setOperationType] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showApply, setShowApply] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (operationType) params.set('operationType', operationType);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m23/uas-operations?${params.toString()}`;
  }, [status, operationType, appliedQ]);

  const listQ = useApiQuery<Page<UasOperationApplication>>(path);

  return (
    <div>
      <PageHeader
        title="UAS Operations"
        subtitle="Flight operation authorisations — VLOS/NIGHT/CONTROLLED_AIRSPACE review, or the full BVLOS SORA + trial-flight pathway (M23 — SCAA)."
        actions={
          <>
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setAppliedQ(q);
              }}
            >
              <input className="field-input !w-36" placeholder="App ref…" value={q} onChange={(e) => setQ(e.target.value)} />
              <select className="field-input !w-auto" value={operationType} onChange={(e) => setOperationType(e.target.value)}>
                <option value="">All types</option>
                {OPERATION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
              <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All states</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {s.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
              <button type="submit" className="btn-secondary">
                Search
              </button>
            </form>
            {canView ? (
              <button className="btn-primary" onClick={() => setShowApply(true)}>
                + Apply for operation
              </button>
            ) : null}
          </>
        }
      />

      {!canView ? <RoleNotice roles={ANY_OFFICER} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No operation applications match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">App ref</th>
                <th className="table-head">Type</th>
                <th className="table-head">Operator</th>
                <th className="table-head">Requested window</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((o) => (
                <tr key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(o.id)}>
                  <td className="table-cell font-mono text-xs font-semibold">{o.appRef}</td>
                  <td className="table-cell text-xs">{o.operationType.replaceAll('_', ' ')}</td>
                  <td className="table-cell font-mono text-xs">{o.operatorId}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDateTime(o.requestedStartAt)}</td>
                  <td className="table-cell">
                    <StatusBadge status={o.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showApply ? (
        <ApplyOperationModal
          onClose={() => setShowApply(false)}
          onCreated={() => {
            setShowApply(false);
            listQ.reload();
          }}
        />
      ) : null}

      {selectedId ? (
        <OperationDetailModal id={selectedId} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} />
      ) : null}
    </div>
  );
}

function ApplyOperationModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [uasRegistrationId, setUasRegistrationId] = useState('');
  const [operatorType, setOperatorType] = useState<'PERSON' | 'ENTITY'>('PERSON');
  const [operatorId, setOperatorId] = useState('');
  const [operationType, setOperationType] = useState<OperationType>('VLOS_STANDARD');
  const [purpose, setPurpose] = useState('');
  const [lng, setLng] = useState('55.45');
  const [lat, setLat] = useState('-4.62');
  const [areaRadiusMetres, setAreaRadiusMetres] = useState('500');
  const [maxAltitudeM, setMaxAltitudeM] = useState('40');
  const [requestedStartAt, setRequestedStartAt] = useState('');
  const [requestedEndAt, setRequestedEndAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m23/uas-operations', {
        uasRegistrationId: uasRegistrationId.trim(),
        operatorType,
        operatorId: operatorId.trim(),
        operationType,
        purpose: purpose.trim(),
        areaCenter: { type: 'Point', coordinates: [Number.parseFloat(lng), Number.parseFloat(lat)] },
        areaRadiusMetres: Number.parseFloat(areaRadiusMetres),
        maxAltitudeM: Number.parseFloat(maxAltitudeM),
        requestedStartAt: new Date(requestedStartAt).toISOString(),
        requestedEndAt: new Date(requestedEndAt).toISOString(),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to apply');
    } finally {
      setBusy(false);
    }
  };

  const valid = uasRegistrationId.trim() && operatorId.trim() && purpose.trim().length >= 3 && requestedStartAt && requestedEndAt;

  return (
    <Modal title="Apply for an operation authorisation" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <p className="mb-3 text-xs text-slate-500">Runs the synchronous no-fly-zone + obstacle-surface airspace check on submit. The linked UAS registration must be REGISTERED.</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="UAS registration id" className="col-span-2 sm:col-span-3">
          <input className="field-input" value={uasRegistrationId} onChange={(e) => setUasRegistrationId(e.target.value)} />
        </Field>
        <Field label="Operator type">
          <select className="field-input" value={operatorType} onChange={(e) => setOperatorType(e.target.value as 'PERSON' | 'ENTITY')}>
            <option value="PERSON">Person</option>
            <option value="ENTITY">Entity</option>
          </select>
        </Field>
        <Field label="Operator id" className="col-span-2">
          <input className="field-input" value={operatorId} onChange={(e) => setOperatorId(e.target.value)} />
        </Field>
        <Field label="Operation type">
          <select className="field-input" value={operationType} onChange={(e) => setOperationType(e.target.value as OperationType)}>
            {OPERATION_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Purpose" className="col-span-2">
          <input className="field-input" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
        </Field>
        <Field label="Area centre longitude">
          <input className="field-input" type="number" step="0.0001" value={lng} onChange={(e) => setLng(e.target.value)} />
        </Field>
        <Field label="Area centre latitude">
          <input className="field-input" type="number" step="0.0001" value={lat} onChange={(e) => setLat(e.target.value)} />
        </Field>
        <Field label="Radius (m)">
          <input className="field-input" type="number" value={areaRadiusMetres} onChange={(e) => setAreaRadiusMetres(e.target.value)} />
        </Field>
        <Field label="Max altitude (m AGL)">
          <input className="field-input" type="number" value={maxAltitudeM} onChange={(e) => setMaxAltitudeM(e.target.value)} />
        </Field>
        <Field label="Requested start">
          <input className="field-input" type="datetime-local" value={requestedStartAt} onChange={(e) => setRequestedStartAt(e.target.value)} />
        </Field>
        <Field label="Requested end">
          <input className="field-input" type="datetime-local" value={requestedEndAt} onChange={(e) => setRequestedEndAt(e.target.value)} />
        </Field>
      </div>
      <button className="btn-primary mt-4" disabled={busy || !valid} onClick={submit}>
        {busy ? 'Submitting…' : 'Submit application'}
      </button>
    </Modal>
  );
}

function OperationDetailModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { persona } = useAuth();
  const canReview = persona ? persona.roles.some((r) => REVIEW_ROLES.includes(r)) : false;
  const isInspector = persona ? persona.roles.some((r) => INSPECTOR_ROLES.includes(r)) : false;
  const q = useApiQuery<UasOperationApplication>(`/api/v1/m23/uas-operations/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decision, setDecision] = useState<'APPROVE' | 'REJECT' | null>(null);
  const [reason, setReason] = useState('');
  const [opsManualDocRef, setOpsManualDocRef] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [trialOutcome, setTrialOutcome] = useState<'PASS' | 'FAIL'>('PASS');
  const [trialReport, setTrialReport] = useState('');
  const [showAction, setShowAction] = useState<string | null>(null);

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const o = q.data;

  return (
    <Modal title={o ? `${o.appRef} — ${o.operationType.replaceAll('_', ' ')}` : 'Operation application'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {o ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="State" value={<StatusBadge status={o.state} />} />
            <Detail label="Operator" value={o.operatorId} mono />
            <Detail label="Radius" value={`${num(o.areaRadiusMetres)} m`} />
            <Detail label="Max altitude" value={`${num(o.maxAltitudeM)} m AGL`} />
            <Detail label="Window" value={`${formatDateTime(o.requestedStartAt)} – ${formatDateTime(o.requestedEndAt)}`} />
            {o.airspaceClear !== undefined && o.airspaceClear !== null ? (
              <Detail label="Airspace check" value={o.airspaceClear ? 'Clear' : 'Intersects a restricted zone / obstacle surface'} />
            ) : null}
          </div>
          <Detail label="Purpose" value={o.purpose} />
          {o.rejectionReason ? <ErrorNote message={`Rejected: ${o.rejectionReason}`} /> : null}
          {o.suspendedReason ? <ErrorNote message={`Suspended: ${o.suspendedReason}`} /> : null}

          {o.state === 'UNDER_REVIEW' ? (
            canReview ? (
              <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="text-sm font-semibold text-slate-700">Review decision</div>
                <div className="flex gap-2">
                  <button className={`btn-success ${decision === 'APPROVE' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDecision('APPROVE')}>
                    Approve
                  </button>
                  <button className={`btn-danger ${decision === 'REJECT' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDecision('REJECT')}>
                    Reject
                  </button>
                </div>
                {decision ? (
                  <>
                    <Field label="Reason">
                      <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button
                      className={decision === 'APPROVE' ? 'btn-success' : 'btn-danger'}
                      disabled={busy || reason.trim().length < 3}
                      onClick={() => call(() => api.post(`/api/v1/m23/uas-operations/${o.id}/review`, { decision, reason: reason.trim() }))}
                    >
                      {busy ? 'Submitting…' : `Confirm ${decision.toLowerCase()}`}
                    </button>
                  </>
                ) : null}
              </div>
            ) : (
              <RoleNotice roles={REVIEW_ROLES} />
            )
          ) : null}

          {o.state === 'RISK_ASSESSMENT' ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <div className="text-sm font-semibold text-slate-700">BVLOS — SORA-lite risk assessment + ops manual</div>
              <Field label="Ops manual doc ref">
                <input className="field-input" value={opsManualDocRef} onChange={(e) => setOpsManualDocRef(e.target.value)} />
              </Field>
              <button
                className="btn-primary"
                disabled={busy || !opsManualDocRef.trim()}
                onClick={() =>
                  call(() =>
                    api.post(`/api/v1/m23/uas-operations/${o.id}/risk-assessment`, {
                      riskAssessment: { groundRiskClass: 'GRC_2', airRiskClass: 'ARC_A', mitigations: [], conceptOfOps: o.purpose },
                      opsManualDocRef: opsManualDocRef.trim(),
                      opsManualSha256: '0'.repeat(64),
                    }),
                  )
                }
              >
                {busy ? 'Submitting…' : 'Submit risk assessment'}
              </button>
            </div>
          ) : null}

          {o.state === 'OPS_MANUAL_REVIEW' ? (
            isInspector ? (
              <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="text-sm font-semibold text-slate-700">Ops manual review</div>
                <div className="flex gap-2">
                  <button className={`btn-success ${decision === 'APPROVE' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDecision('APPROVE')}>
                    Approve
                  </button>
                  <button className={`btn-danger ${decision === 'REJECT' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDecision('REJECT')}>
                    Reject
                  </button>
                </div>
                {decision ? (
                  <>
                    <Field label="Note">
                      <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button
                      className={decision === 'APPROVE' ? 'btn-success' : 'btn-danger'}
                      disabled={busy || reason.trim().length < 3}
                      onClick={() => call(() => api.post(`/api/v1/m23/uas-operations/${o.id}/ops-manual-review`, { decision, note: reason.trim() }))}
                    >
                      {busy ? 'Submitting…' : `Confirm ${decision.toLowerCase()}`}
                    </button>
                  </>
                ) : null}
              </div>
            ) : (
              <RoleNotice roles={INSPECTOR_ROLES} />
            )
          ) : null}

          {o.state === 'TRIAL_FLIGHT' ? (
            isInspector ? (
              !o.trialFlightScheduledAt ? (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <div className="text-sm font-semibold text-slate-700">Schedule the Ezzy Courier pathway trial flight</div>
                  <Field label="Scheduled at">
                    <input className="field-input" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
                  </Field>
                  <button
                    className="btn-primary"
                    disabled={busy || !scheduledAt}
                    onClick={() => call(() => api.post(`/api/v1/m23/uas-operations/${o.id}/trial-flight/schedule`, { scheduledAt: new Date(scheduledAt).toISOString() }))}
                  >
                    {busy ? 'Scheduling…' : 'Schedule trial flight'}
                  </button>
                </div>
              ) : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <div className="text-sm font-semibold text-slate-700">Trial flight report — scheduled {formatDateTime(o.trialFlightScheduledAt)}</div>
                  <div className="flex gap-2">
                    <button className={`btn-success ${trialOutcome === 'PASS' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setTrialOutcome('PASS')}>
                      Pass
                    </button>
                    <button className={`btn-danger ${trialOutcome === 'FAIL' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setTrialOutcome('FAIL')}>
                      Fail
                    </button>
                  </div>
                  <Field label="Report">
                    <textarea className="field-input" rows={2} value={trialReport} onChange={(e) => setTrialReport(e.target.value)} />
                  </Field>
                  <button
                    className={trialOutcome === 'PASS' ? 'btn-success' : 'btn-danger'}
                    disabled={busy || trialReport.trim().length < 3}
                    onClick={() => call(() => api.post(`/api/v1/m23/uas-operations/${o.id}/trial-flight/report`, { outcome: trialOutcome, report: trialReport.trim() }))}
                  >
                    {busy ? 'Submitting…' : `Confirm ${trialOutcome.toLowerCase()}`}
                  </button>
                </div>
              )
            ) : (
              <RoleNotice roles={INSPECTOR_ROLES} />
            )
          ) : null}

          {canReview ? (
            <div className="flex flex-wrap gap-2">
              {o.state === 'AUTHORIZED' ? (
                showAction === 'suspend' ? (
                  <div className="flex items-end gap-2">
                    <Field label="Reason">
                      <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button className="btn-danger" disabled={busy || reason.trim().length < 3} onClick={() => call(() => api.post(`/api/v1/m23/uas-operations/${o.id}/suspend`, { reason: reason.trim() }))}>
                      Confirm
                    </button>
                  </div>
                ) : (
                  <button className="btn-danger" onClick={() => setShowAction('suspend')}>
                    Suspend
                  </button>
                )
              ) : null}
              {o.state === 'SUSPENDED' ? (
                <button className="btn-success" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m23/uas-operations/${o.id}/reinstate`))}>
                  Reinstate
                </button>
              ) : null}
              {o.state === 'AUTHORIZED' || o.state === 'SUSPENDED' ? (
                showAction === 'revoke' ? (
                  <div className="flex items-end gap-2">
                    <Field label="Reason">
                      <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button className="btn-danger" disabled={busy || reason.trim().length < 3} onClick={() => call(() => api.post(`/api/v1/m23/uas-operations/${o.id}/revoke`, { reason: reason.trim() }))}>
                      Confirm
                    </button>
                  </div>
                ) : (
                  <button className="btn-danger" onClick={() => setShowAction('revoke')}>
                    Revoke
                  </button>
                )
              ) : null}
            </div>
          ) : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">Filed {formatDateTime(o.createdAt)}</div>
        </div>
      ) : null}
    </Modal>
  );
}
